/**
 * Persistent banner for "Learner mode" — an app admin experiencing the learner
 * UI (catalog, pathway page, module reader) without signing out of the admin
 * account. Navigation, tiles and feature gates follow the learner role; every
 * database read still runs under the admin's own credentials and RLS.
 */
import { useNavigate } from "react-router-dom";
import { GraduationCap, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUserRole } from "@/hooks/useUserRole";

export const LearnerPreviewBanner = () => {
  const { isRealAppAdmin, viewAsRole, impersonatedUser, setViewAsRole } = useUserRole();
  const navigate = useNavigate();

  // The account-level impersonation banner takes precedence.
  if (!isRealAppAdmin || impersonatedUser || viewAsRole !== "player_viewer") return null;

  const exit = () => {
    setViewAsRole(null);
    navigate("/app/admin", { replace: true });
  };

  return (
    <div className="sticky top-0 z-50 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-primary/40 bg-primary/10 px-4 py-2 text-sm">
      <span className="flex items-center gap-2 font-medium text-primary">
        <GraduationCap className="h-4 w-4 shrink-0" />
        Learner mode
      </span>
      <span className="text-xs text-muted-foreground">
        You are still signed in as an admin — only the UI follows the learner role.
      </span>
      <Button size="sm" variant="outline" className="h-7 gap-1" onClick={exit}>
        <X className="h-3.5 w-3.5" />
        Exit learner mode
      </Button>
    </div>
  );
};
